import {
  getActiveMerchantUuid,
  getRecordMerchant,
  readGroupSession
} from 'lib/merchant-group'
import { parseCookies } from 'nookies'

const DEFAULT_INVOICE_DOMAIN = 'fatura.clientbase.com.br'
const DEFAULT_PORTAL_DOMAIN = 'basedeclientes.com.br'

/**
 * Domínio da fatura de uma cobrança. Fora do grupo é o da sessão (cookie). Na
 * visão multi-empresas (SC-10999) a cobrança pode ser de outra empresa do
 * grupo, que tem o próprio white-label: vale o da empresa do registro (ou da
 * empresa ativa, quando aberta num detalhe), vindo da sessão do grupo.
 */
const invoiceDomainFor = (uuid?: string): string | undefined => {
  const group = readGroupSession()
  if (group) {
    const merchantUuid = getRecordMerchant(uuid) || getActiveMerchantUuid()
    const merchant = group.merchants.find(
      (item) => (item.merchant_uuid || item.uuid) === merchantUuid
    )
    if (merchant && 'custom_invoice_domain' in merchant) {
      return merchant.custom_invoice_domain || undefined
    }
  }
  const { ['nextauth.custom_invoice_domain']: customDomain } = parseCookies()
  return customDomain && customDomain !== 'null' ? customDomain : undefined
}

export function getInvoiceUrl(uuid: string): string {
  const domain = invoiceDomainFor(uuid) || DEFAULT_INVOICE_DOMAIN
  return `https://${domain}/${uuid}`
}

export function getCancellationLetterUrl(uuid: string): string {
  const customDomain = invoiceDomainFor(uuid)
  const domain =
    customDomain && customDomain !== 'null'
      ? customDomain
      : DEFAULT_INVOICE_DOMAIN
  return `https://${domain}/carta-cancelamento/${uuid}`
}

export function getPortalBaseUrl(subdomain?: string | null): string {
  const {
    ['nextauth.custom_portal_domain']: customDomain,
    ['nextauth.subdomain']: cookieSubdomain
  } = parseCookies()

  if (customDomain && customDomain !== 'null') {
    return `https://${customDomain}`
  }

  const effectiveSubdomain = subdomain || cookieSubdomain || 'portal'

  const effective =
    effectiveSubdomain !== 'null' ? effectiveSubdomain : 'portal'

  return `https://${effective}.` + DEFAULT_PORTAL_DOMAIN
}

export function getPortalAutoLoginUrl(
  token: string,
  subdomain?: string | null
): string {
  return `${getPortalBaseUrl(subdomain)}/auto-login?auth=${token}`
}

export function getPortalActivateAccountUrl(
  token: string,
  subdomain?: string | null
): string {
  return `${getPortalBaseUrl(
    subdomain
  )}/login/ativar-conta-cliente?auth=${token}`
}

export function getCheckoutUrl(
  uuid: string,
  subdomain?: string | null
): string {
  return `${getPortalBaseUrl(subdomain)}/checkout/${uuid}`
}

/**
 * Converts a portal URL that may use the default domain to use the custom domain if available.
 * Useful when the API returns URLs with hardcoded domains.
 */
export function convertToCustomPortalUrl(url: string): string {
  const { ['nextauth.custom_portal_domain']: customDomain } = parseCookies()

  if (!customDomain || customDomain === 'null') {
    return url
  }

  // Replace any basedeclientes.com.br subdomain with the custom domain
  return url.replace(
    /https?:\/\/[a-zA-Z0-9-]+\.basedeclientes\.com\.br/,
    `https://${customDomain}`
  )
}
